import { Node, VariantText } from '../types/narrative';

// Types for serialized world variant content
export interface SerializedVariantContent {
  goodWorld?: {
    variants: VariantText[];
    placeholderPosition?: number;
  };
  badWorld?: {
    variants: VariantText[];
    placeholderPosition?: number;
  };
}

// Types for nodes with world-specific content
export interface WorldSpecificNode extends Node {
  variantContent?: {
    goodWorld?: {
      variants: VariantText[];
      placeholderPosition?: number;
    };
    badWorld?: {
      variants: VariantText[];
      placeholderPosition?: number;
    };
  };
}

/**
 * Serializes variant content for storage
 * Ensures proper structure and handles missing data
 */
export function serializeVariantContent(
  variantContent: WorldSpecificNode['variantContent']
): SerializedVariantContent | undefined {
  if (!variantContent) return undefined;

  const serialized: SerializedVariantContent = {};

  // Serialize goodWorld variants
  if (variantContent.goodWorld) {
    serialized.goodWorld = {
      variants: variantContent.goodWorld.variants.map((v) => ({
        choiceId: v.choiceId || '',
        choiceText: v.choiceText || '',
        variantLine: v.variantLine || '',
      })),
      placeholderPosition: variantContent.goodWorld.placeholderPosition ?? 0,
    };
  }

  // Serialize badWorld variants
  if (variantContent.badWorld) {
    serialized.badWorld = {
      variants: variantContent.badWorld.variants.map((v) => ({
        choiceId: v.choiceId || '',
        choiceText: v.choiceText || '',
        variantLine: v.variantLine || '',
      })),
      placeholderPosition: variantContent.badWorld.placeholderPosition ?? 0,
    };
  }

  return serialized;
}

/**
 * Deserializes variant content from storage
 * Validates structure and provides defaults for missing data
 */
export function deserializeVariantContent(
  serialized: any
): WorldSpecificNode['variantContent'] | undefined {
  if (!serialized || typeof serialized !== 'object') return undefined;

  const deserialized: WorldSpecificNode['variantContent'] = {};

  // Deserialize goodWorld
  if (serialized.goodWorld && typeof serialized.goodWorld === 'object') {
    const variants = Array.isArray(serialized.goodWorld.variants)
      ? serialized.goodWorld.variants
          .filter((v: any) => v && typeof v === 'object')
          .map((v: any) => ({
            choiceId: String(v.choiceId || ''),
            choiceText: String(v.choiceText || ''),
            variantLine: String(v.variantLine || ''),
          }))
      : [];

    deserialized.goodWorld = {
      variants,
      placeholderPosition:
        typeof serialized.goodWorld.placeholderPosition === 'number'
          ? serialized.goodWorld.placeholderPosition
          : 0,
    };
  }

  // Deserialize badWorld
  if (serialized.badWorld && typeof serialized.badWorld === 'object') {
    const variants = Array.isArray(serialized.badWorld.variants)
      ? serialized.badWorld.variants
          .filter((v: any) => v && typeof v === 'object')
          .map((v: any) => ({
            choiceId: String(v.choiceId || ''),
            choiceText: String(v.choiceText || ''),
            variantLine: String(v.variantLine || ''),
          }))
      : [];

    deserialized.badWorld = {
      variants,
      placeholderPosition:
        typeof serialized.badWorld.placeholderPosition === 'number'
          ? serialized.badWorld.placeholderPosition
          : 0,
    };
  }

  // Return undefined if no valid world data was found
  if (!deserialized.goodWorld && !deserialized.badWorld) {
    return undefined;
  }

  return deserialized;
}

/**
 * Serializes a node with world-specific content
 * Handles all world-specific fields including variant content
 */
export function serializeWorldNode(node: Node): any {
  const serialized: any = { ...node };

  // Serialize variant content if present
  if (node.variantContent) {
    serialized.variantContent = serializeVariantContent(node.variantContent);
  }

  // Ensure world-specific text fields are properly serialized
  const worldTextFields = [
    'textBeforeGoodWorld',
    'textAfterGoodWorld',
    'textBeforeBadWorld',
    'textAfterBadWorld',
    'textMainGoodWorld',
    'textMainBadWorld',
  ];

  worldTextFields.forEach((field) => {
    if (field in node) {
      serialized[field] = String(node[field as keyof Node] || '');
    }
  });

  // Ensure world-specific choice arrays are properly serialized
  if (node.choicesGoodWorld) {
    serialized.choicesGoodWorld = node.choicesGoodWorld.map((choice) => ({
      id: choice.id,
      text: choice.text || '',
      npcResponse: choice.npcResponse || '',
      goto: choice.goto,
      setsVariable: choice.setsVariable ? { ...choice.setsVariable } : undefined,
    }));
  }

  if (node.choicesBadWorld) {
    serialized.choicesBadWorld = node.choicesBadWorld.map((choice) => ({
      id: choice.id,
      text: choice.text || '',
      npcResponse: choice.npcResponse || '',
      goto: choice.goto,
      setsVariable: choice.setsVariable ? { ...choice.setsVariable } : undefined,
    }));
  }

  return serialized;
}

/**
 * Deserializes a node with world-specific content
 * Validates and restores all world-specific fields
 */
export function deserializeWorldNode(serialized: any): Node {
  const node: Node = {
    id: serialized.id || '',
    segmentId: serialized.segmentId || 1,
    type: serialized.type || 'Standard',
  };

  // Copy basic fields
  const basicFields = [
    'idAlias',
    'label',
    'guidance',
    'descPreview',
    'text',
    'parentIds',
    'childIds',
    'completionStatus',
  ];

  basicFields.forEach((field) => {
    if (field in serialized) {
      (node as any)[field] = serialized[field];
    }
  });

  // Deserialize variant content
  if (serialized.variantContent) {
    node.variantContent = deserializeVariantContent(serialized.variantContent);
  }

  // Deserialize world-specific text fields
  const worldTextFields = [
    'textBeforeGoodWorld',
    'textAfterGoodWorld',
    'textBeforeBadWorld',
    'textAfterBadWorld',
    'textMainGoodWorld',
    'textMainBadWorld',
  ];

  worldTextFields.forEach((field) => {
    if (field in serialized && serialized[field] !== undefined) {
      (node as any)[field] = String(serialized[field]);
    }
  });

  // Deserialize standard choices
  if (serialized.choices && Array.isArray(serialized.choices)) {
    node.choices = serialized.choices.map((choice: any) => ({
      id: choice.id || '',
      text: choice.text || '',
      npcResponse: choice.npcResponse,
      goto: choice.goto || '',
      setsVariable: choice.setsVariable,
    }));
  }

  // Deserialize world-specific choices
  if (serialized.choicesGoodWorld && Array.isArray(serialized.choicesGoodWorld)) {
    node.choicesGoodWorld = serialized.choicesGoodWorld.map((choice: any) => ({
      id: choice.id || '',
      text: choice.text || '',
      npcResponse: choice.npcResponse,
      goto: choice.goto || '',
      setsVariable: choice.setsVariable,
    }));
  }

  if (serialized.choicesBadWorld && Array.isArray(serialized.choicesBadWorld)) {
    node.choicesBadWorld = serialized.choicesBadWorld.map((choice: any) => ({
      id: choice.id || '',
      text: choice.text || '',
      npcResponse: choice.npcResponse,
      goto: choice.goto || '',
      setsVariable: choice.setsVariable,
    }));
  }

  // Deserialize conditional text blocks
  if (serialized.conditionalTextBlocks) {
    node.conditionalTextBlocks = {
      goodWorld: serialized.conditionalTextBlocks.goodWorld || [],
      badWorld: serialized.conditionalTextBlocks.badWorld || [],
    };
  }

  return node;
}

/**
 * Serializes the entire narrative state, handling world-specific nodes
 */
export function serializeNarrativeState(narrativeState: any): any {
  if (!narrativeState) return narrativeState;

  const serialized = { ...narrativeState };

  // Serialize all nodes, with special handling for world-specific nodes
  if (narrativeState.nodes && typeof narrativeState.nodes === 'object') {
    serialized.nodes = {};
    Object.entries(narrativeState.nodes).forEach(([nodeId, node]) => {
      if (node && typeof node === 'object') {
        // Check if this is a node with world-specific content
        const typedNode = node as Node;
        if (
          typedNode.variantContent ||
          typedNode.textBeforeGoodWorld !== undefined ||
          typedNode.textBeforeBadWorld !== undefined ||
          typedNode.choicesGoodWorld ||
          typedNode.choicesBadWorld
        ) {
          serialized.nodes[nodeId] = serializeWorldNode(typedNode);
        } else {
          // Regular node, just copy
          serialized.nodes[nodeId] = { ...typedNode };
        }
      }
    });
  }

  return serialized;
}

/**
 * Deserializes the entire narrative state, handling world-specific nodes
 */
export function deserializeNarrativeState(serialized: any): any {
  if (!serialized || typeof serialized !== 'object') return serialized;

  const deserialized = { ...serialized };

  // Deserialize all nodes
  if (serialized.nodes && typeof serialized.nodes === 'object') {
    deserialized.nodes = {};
    Object.entries(serialized.nodes).forEach(([nodeId, node]) => {
      if (node && typeof node === 'object') {
        // Check if this might be a world-specific node
        const serializedNode = node as any;
        if (
          serializedNode.variantContent ||
          serializedNode.textBeforeGoodWorld !== undefined ||
          serializedNode.textBeforeBadWorld !== undefined ||
          serializedNode.choicesGoodWorld ||
          serializedNode.choicesBadWorld
        ) {
          deserialized.nodes[nodeId] = deserializeWorldNode(serializedNode);
        } else {
          // Regular node
          deserialized.nodes[nodeId] = { ...serializedNode };
        }
      }
    });
  }

  return deserialized;
}
